import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Student, InsertStudent, Run } from "../../lib/supabase";
import { supabase } from "../../lib/supabase";
import { ENTITY_NAMES } from "../../constants/entities";
import toast from "react-hot-toast";
import { Listbox } from "../ui/Listbox";
import { formatDaysOfWeek } from "../../utils/runs";

interface StudentFormProps {
  defaultValues?: Student;
  onSubmit: (data: InsertStudent) => void;
  isLoading: boolean;
}

interface RunWithDetails extends Run {
  series?: {
    title: string;
  };
}

interface Series {
  id: string;
  title: string;
}

const StudentForm: React.FC<StudentFormProps> = ({
  defaultValues,
  onSubmit,
  isLoading,
}) => {
  const [runs, setRuns] = useState<RunWithDetails[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(true);
  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<InsertStudent>({
    defaultValues: defaultValues || {
      codername: "",
      age: 0,
      parentname: "",
      email: "",
      phone: "",
      run_id: "",
      notes: "",
      school: "",
    },
  });

  // Fetch all series on mount
  useEffect(() => {
    const fetchSeries = async () => {
      const { data, error } = await supabase
        .from("series")
        .select("id, title")
        .order("title");
      if (error) {
        toast.error("Failed to load series");
        return;
      }
      setSeriesList(data || []);

      // If editing and we have a run_id, find the associated series
      if (defaultValues?.run_id && data) {
        const { data: runData } = await supabase
          .from("runs")
          .select("series_id")
          .eq("id", defaultValues.run_id)
          .single();

        if (runData) {
          setSelectedSeriesId(runData.series_id);
        }
      }
    };
    fetchSeries();
  }, [defaultValues]);

  // Fetch runs when selectedSeriesId changes
  useEffect(() => {
    if (!selectedSeriesId) {
      setRuns([]);
      return;
    }
    const fetchRuns = async () => {
      setIsLoadingRuns(true);
      const { data, error } = await supabase
        .from("runs")
        .select("*, series:series_id(title)")
        .eq("active", true)
        .eq("series_id", selectedSeriesId)
        .order("day_of_week", { ascending: true })
        .order("start_time", { ascending: true });
      if (error) {
        toast.error("Failed to load runs");
        setRuns([]);
      } else {
        setRuns(data || []);
      }
      setIsLoadingRuns(false);
    };
    fetchRuns();
  }, [selectedSeriesId]);

  // Format day and time for display
  const formatRunInfo = (run: RunWithDetails) => {
    const formatTime = (timeString: string) => {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `Group ${run.name} - ${formatDaysOfWeek(
      run.day_of_week
    )} at ${formatTime(run.start_time)}`;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
      <h2 className="">Student Information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="codername" className="label">
            Student Name <span className="text-error-500">*</span>
          </label>
          <input
            id="codername"
            type="text"
            className={`input ${
              errors.codername
                ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                : ""
            }`}
            {...register("codername", {
              required: "Student name is required",
            })}
          />
          {errors.codername && (
            <p className="mt-1 text-sm text-error-500">
              {errors.codername.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="age" className="label">
            Age <span className="text-error-500">*</span>
          </label>
          <input
            id="age"
            type="number"
            min="5"
            max="18"
            className={`input ${
              errors.age
                ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                : ""
            }`}
            {...register("age", {
              required: "Age is required",
              min: { value: 5, message: "Age must be at least 5" },
              max: { value: 18, message: "Age must be less than 18" },
              valueAsNumber: true,
            })}
          />
          {errors.age && (
            <p className="mt-1 text-sm text-error-500">{errors.age.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="school" className="label">
            School
          </label>
          <input
            id="school"
            type="text"
            className="input"
            placeholder=""
            {...register("school")}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-neutral-700">
            Select a Series
          </label>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-grow">
              <Listbox
                options={seriesList.map((series) => ({
                  value: series.id,
                  label: series.title,
                }))}
                value={selectedSeriesId}
                onChange={(val) => {
                  setSelectedSeriesId(val);
                  setValue("run_id", ""); // Reset run when series changes
                }}
              />
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-neutral-700">
            Select a {ENTITY_NAMES.RUN}
          </label>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-grow">
              <Listbox
                options={runs.map((run) => ({
                  value: run.id,
                  label: formatRunInfo(run),
                }))}
                value={watch("run_id") || ""}
                onChange={(val) => setValue("run_id", val)}
                disabled={isLoadingRuns || !selectedSeriesId}
              />
            </div>
          </div>

          {!selectedSeriesId && (
            <p className="mt-1 text-sm text-neutral-500">
              Select a series first to see available {ENTITY_NAMES.RUN}s
            </p>
          )}
          {errors.run_id && (
            <p className="mt-1 text-sm text-error-500">
              {errors.run_id.message}
            </p>
          )}
        </div>
      </div>

      <h2 className="pt-6">Parent Information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="parentname" className="label">
            Parent Name <span className="text-error-500">*</span>
          </label>
          <input
            id="parentname"
            type="text"
            className={`input ${
              errors.parentname
                ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                : ""
            }`}
            {...register("parentname", {
              required: "Parent name is required",
            })}
          />
          {errors.parentname && (
            <p className="mt-1 text-sm text-error-500">
              {errors.parentname.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="label">
            Email <span className="text-error-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            className={`input ${
              errors.email
                ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                : ""
            }`}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error-500">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="label">
            Phone <span className="text-error-500">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            className={`input ${
              errors.phone
                ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                : ""
            }`}
            {...register("phone", {
              required: "Phone number is required",
            })}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-error-500">
              {errors.phone.message}
            </p>
          )}
        </div>
      </div>

      <h2 className="pt-6">Notes</h2>
      <div>
        <textarea
          id="notes"
          rows={4}
          className="input"
          placeholder="Add any notes about this student..."
          {...register("notes")}
        />
        <p className="mt-1 text-sm text-neutral-500">
          Optional notes about the student (behavior, preferences, etc.)
        </p>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="btn btn-outline"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className={`btn btn-primary ${
            isLoading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isLoading ? (
            <>
              <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </form>
  );
};

export default StudentForm;
